import React, { useState } from "react";
import { ServicePageHero, FeatureGrid, ProcessSteps, CTAStripe } from "@/components/sections/Shared";
import QuoteDialog from "@/components/QuoteDialog";
import { IMAGES } from "@/lib/business";
import { Blinds, Home, Layers, Sparkles, Sun, Palette } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import SEO, { serviceJsonLd } from "@/components/SEO";

const FEATURES = [
    { icon: Blinds, title: "Custom Blinds", description: "Zebra, roller, roman, cellular, wood and faux-wood blinds tailored to every window." },
    { icon: Layers, title: "Shades", description: "Light-filtering to blackout shades in a wide range of fabrics, textures and tones." },
    { icon: Home, title: "Shutters", description: "Elegant, durable plantation-style shutters crafted to match your interiors perfectly." },
    { icon: Sparkles, title: "Motorised Options", description: "Smart, motorised blinds with remote, app, or voice control — safer around kids and pets." },
    { icon: Sun, title: "Drapery & Curtains", description: "Custom drapery that frames your rooms beautifully while controlling light and privacy." },
    { icon: Palette, title: "Design Guidance", description: "Honest, practical styling advice — we help you pick finishes that work for real life." },
];

const FAQS = [
    {
        q: "Do you do measurements and installation?",
        a: "Yes — free in-home measurement, professional installation, and tidy cleanup are all included in our quote.",
    },
    {
        q: "How long does a typical project take?",
        a: "Most window covering projects are manufactured and installed within 2-4 weeks from quote approval, depending on materials.",
    },
    {
        q: "Are motorised blinds worth it?",
        a: "Absolutely — they're safer (no cords), more convenient, and can be scheduled to open or close automatically with the sun.",
    },
    {
        q: "Do you work with designers?",
        a: "Yes. We work directly with homeowners, interior designers, and builders — we can spec to any design brief.",
    },
];

export default function BlindsShutters() {
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <SEO
                title="Custom Blinds, Shades & Shutters in the GTA"
                description="Made-to-measure blinds, shades, shutters and drapery with free in-home measurement and professional installation across the Greater Toronto Area."
                path="/blinds-shutters"
                keywords="custom blinds GTA, plantation shutters Toronto, zebra blinds, motorised blinds, roman shades, drapery Mississauga"
                jsonLd={serviceJsonLd(
                    "Custom Blinds & Shutters",
                    "Custom blinds, shades, shutters, and drapery installed across the Greater Toronto Area.",
                )}
            />
            <ServicePageHero
                eyebrow="Blinds & Shutters"
                title="Custom blinds, shades & shutters — beautifully fitted."
                subtitle="Elevate every room with made-to-measure window coverings. Modern, traditional, motorised — we tailor to your space, taste, and budget."
                image="https://customer-assets.emergentagent.com/job_gtasecure-staging/artifacts/9mh406pm_zebra%20blinds%20gta%20secure.png"
                defaultQuoteService="Blinds & Shutters"
                bullets={[
                    "Free in-home measurement and consultation",
                    "Honest, pressure-free design guidance",
                    "Full range: blinds, shades, shutters, drapery",
                    "Motorised and smart-home compatible options",
                ]}
            />

            <section className="section bg-white">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Our range
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            A window covering for every room.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <FeatureGrid items={FEATURES} />
                    </div>
                </div>
            </section>

            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> How we work
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            From consultation to curtain call.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <ProcessSteps
                            steps={[
                                { title: "In-home consultation", description: "We visit your space, listen to your brief, and show you samples and options that fit your home." },
                                { title: "Precise measurement", description: "Every window is professionally measured to ensure a perfect custom fit." },
                                { title: "Install & finish", description: "Clean installation, smooth operation, and a beautiful finish you'll love every day." },
                            ]}
                        />
                    </div>
                </div>
            </section>

            <section className="section bg-white">
                <div className="container-x">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                        <div className="max-w-xl">
                            <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                                <span className="gold-rule" /> Portfolio
                            </p>
                            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                                Beautifully finished projects.
                            </h2>
                        </div>
                    </div>
                    <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                        {IMAGES.gallery.map((src, idx) => (
                            <div
                                key={src}
                                data-testid={`blinds-gallery-${idx}`}
                                className="aspect-[4/3] overflow-hidden rounded-2xl bg-navy/5"
                            >
                                <img
                                    src={src}
                                    alt={`Project ${idx + 1}`}
                                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-700"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section bg-[#FDFCFB]">
                <div className="container-x grid lg:grid-cols-[1fr_1.3fr] gap-12">
                    <div>
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> FAQ
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Blinds &amp; shutters, explained.
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((f, i) => (
                            <AccordionItem value={`b-${i}`} key={f.q} className="border-b border-gray-200">
                                <AccordionTrigger className="text-left font-semibold text-navy text-base md:text-lg py-5 hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            <CTAStripe
                heading="Dress every window beautifully."
                subheading="Free in-home measurement and honest design guidance."
                ctaLabel="Book a Consultation"
                onCta={() => setQuoteOpen(true)}
            />
            <QuoteDialog
                open={quoteOpen}
                onOpenChange={setQuoteOpen}
                defaultService="Blinds & Shutters"
            />
        </>
    );
}
